import { useCallback, useState } from 'react';

import { DateRange } from '@mint-lib/tw-components/patterns';

import type { DateRangeModel } from '../types/filters.js';
import {
  getModelFromRange,
  getRangeFromModel,
  isDateRangeModelsEqual,
} from '../utils/filters.js';

const useDateRangePicker = (model: DateRangeModel | null) => {
  const [selectedRange, setSelectedRange] = useState<DateRange>(
    getRangeFromModel(model),
  );
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
    selectedRange.from,
  );

  const syncLocalStateWithModel = useCallback(() => {
    const modelFromSelected = getModelFromRange(selectedRange);
    const today = new Date();

    if (isDateRangeModelsEqual(model, modelFromSelected)) {
      const month = selectedRange.from ?? today;
      setSelectedMonth(month);
      return;
    }

    const rangeFromModel = getRangeFromModel(model);
    const month = rangeFromModel.from ?? today;

    setSelectedRange(rangeFromModel);
    setSelectedMonth(month);
  }, [model, selectedRange]);

  const changeRange = useCallback((dateRange?: DateRange) => {
    setSelectedRange({
      from: dateRange?.from,
      to: dateRange?.to,
    });
  }, []);

  const resetLocalState = useCallback(() => {
    setSelectedRange({
      from: undefined,
      to: undefined,
    });
    setSelectedMonth(new Date());
  }, []);

  return {
    selectedRange,
    selectedMonth,
    changeRange,
    changeMonth: setSelectedMonth,
    resetLocalState,
    syncLocalStateWithModel,
  };
};

export default useDateRangePicker;
